import { useState } from 'react'
import { localDayKey, previousPeriod } from '@wf/shared'
import { useAttendanceToday } from '../attendance/useAttendance'
import { formatDateDE, formatHMS } from '../time/format'
import { isoWeekKey, monthLabel, monthRange, shiftDay, weekRange } from './timeMath'
import { ArbzgAverageTeamCard } from './ArbzgAverageTeamCard'
import { useTeamReports, useTeamTimesheets } from './useZeiten'

// Mo–So: Sonntagsarbeit muss in den Tages-Spalten auftauchen — sie steckt ohnehin im Ist/Saldo
// (Review-Fund; eine Spalte mehr ist ehrlicher als eine stille Differenz).
const WD = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

/** ±Saldo lesbar + Farbe (Design 1c: grün/rot). */
function saldoParts(ist: number, soll: number): { text: string; cls: string } {
  const diff = ist - soll
  if (diff === 0) return { text: '±00:00:00', cls: '' }
  return diff > 0 ? { text: `+${formatHMS(diff)}`, cls: 'wf-ztweek__diff--plus' } : { text: `−${formatHMS(-diff)}`, cls: 'wf-ztweek__diff--minus' }
}

/**
 * „Auswertung" nach Simons Design (1c): Woche/Monat umschaltbar, eine Zeile je Mitarbeiter mit
 * Tages- (Woche) bzw. Wochen-Spalten (Monat), Soll/Ist/Saldo aus dem Timesheet (Dienstplan vs.
 * Stempel, Server-Wahrheit). Das kumulierte Überstunden-KONTO aus dem Design braucht eine eigene
 * Backend-Welle — bis dahin zeigt die Tabelle ehrlich den Saldo des laufenden Zeitraums.
 * Nur isBoss (fremde Timesheets/Reports sind serverseitig Boss-gated).
 */
export function AuswertungPanel({ onCorrect }: { onCorrect: (userId: number) => void }) {
  const [mode, setMode] = useState<'woche' | 'monat'>('woche')
  const team = useAttendanceToday()
  const today = localDayKey(Date.now())
  const range = mode === 'woche' ? weekRange(today) : monthRange(today.slice(0, 7))
  const staff = team.data?.entries ?? []
  const ids = staff.map((e) => e.userId)
  const timesheets = useTeamTimesheets(ids, range.from, range.to, staff.length > 0)
  // Tages-Ist je MA braucht die Report-Tageszeilen (das Timesheet liefert nur Wochen-Summen).
  const reports = useTeamReports(ids, range.from, range.to, mode === 'woche' && staff.length > 0)

  const weekDays = WD.map((_, i) => shiftDay(range.from, i))
  // KW-Kopf aus der ERSTEN erfolgreichen Timesheet-Antwort — timesheets[0] kann noch laden/fehlschlagen.
  const monthWeeks = timesheets.find((q) => q.data)?.data?.weeks.map((w) => w.weekStart) ?? []
  const anyLoading = team.isLoading || timesheets.some((q) => q.isLoading) || (mode === 'woche' && reports.some((q) => q.isLoading))
  const anyError = team.isError || timesheets.some((q) => q.isError) || (mode === 'woche' && reports.some((q) => q.isError))

  return (
    <div className="wf-view wf-zstack">
      <div className="wf-zshead">
        <div>
          <div className="wf-zshead__greet">Auswertung</div>
          <div className="wf-zshead__date">
            {mode === 'woche'
              ? `Woche ab ${formatDateDE(range.from)} (laufend) · Soll/Ist pro Mitarbeiter`
              : `${monthLabel(today.slice(0, 7))} (laufend) · Wochen-Summen`}
          </div>
        </div>
        <div className="wf-chips" role="group" aria-label="Zeitraum">
          <button type="button" className={mode === 'woche' ? 'wf-chip wf-chip--active' : 'wf-chip'} aria-pressed={mode === 'woche'} onClick={() => setMode('woche')}>
            Woche
          </button>
          <button type="button" className={mode === 'monat' ? 'wf-chip wf-chip--active' : 'wf-chip'} aria-pressed={mode === 'monat'} onClick={() => setMode('monat')}>
            Monat
          </button>
        </div>
      </div>

      {anyLoading ? <div className="muted">Auswertung wird geladen…</div> : null}
      {team.isError ? (
        <p className="hint" role="status">
          Team-Liste nicht verfügbar — ohne sie kann die Auswertung keine Mitarbeiter anzeigen.
        </p>
      ) : null}
      {anyError ? (
        <div className="error" role="alert">
          Auswertung konnte nicht vollständig geladen werden — fehlende Werte erscheinen als „…", nicht als „–".
        </div>
      ) : null}
      {team.data && staff.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Mitarbeiter</div>
        </div>
      ) : null}

      {/* Bewusst KEIN role="table": ohne vollständige row/cell-Semantik wäre das für Screenreader
          kaputter als eine schlichte beschriftete Liste (Review-Fund a11y). */}
      {staff.length > 0 ? (
        <div className="wf-zsaus" aria-label="Soll/Ist je Mitarbeiter">
          <div className="wf-zsaus__row wf-zsaus__row--head">
            <span className="wf-zsaus__name">Mitarbeiter</span>
            {(mode === 'woche' ? WD : monthWeeks.map((w) => isoWeekKey(w).replace(/^\d{4}-/, 'KW '))).map((label, i) => (
              <span key={i} className="wf-zsaus__val">
                {String(label).replace('KW W', 'KW ')}
              </span>
            ))}
            <span className="wf-zsaus__val">Soll</span>
            <span className="wf-zsaus__val">Ist</span>
            <span className="wf-zsaus__val">Saldo</span>
            <span className="wf-zsaus__edit" />
          </div>
          {staff.map((entry, i) => {
            const ts = timesheets[i]?.data
            const report = reports[i]?.data
            // „–" heißt ausschließlich „keine Stempel" — Laden/Fehler zeigen „…" (Review-Fund: die
            // Tabelle darf während des Ladens nicht wie ein leerer Monat aussehen).
            const rowPending = mode === 'woche' ? !report : !ts
            const vals: string[] = rowPending
              ? WD.map(() => '…')
              : mode === 'woche'
                ? weekDays.map((day) => {
                    const sec = report?.days.find((d) => d.day === day)?.workedSeconds ?? 0
                    return sec ? formatHMS(sec) : '–'
                  })
                : (ts?.weeks ?? []).map((w) => (w.istSeconds ? formatHMS(w.istSeconds) : '–'))
            const soll = ts?.totalSollSeconds ?? 0
            const ist = ts?.totalIstSeconds ?? 0
            const saldo = saldoParts(ist, soll)
            return (
              <div key={entry.userId} className="wf-zsaus__row">
                <span className="wf-zsaus__name">
                  <span className="wf-zstl__avatar" aria-hidden="true">
                    {entry.name
                      .split(' ')
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  {entry.name}
                </span>
                {vals.map((v, k) => (
                  <span key={k} className="wf-zsaus__val wf-mono">
                    {v}
                  </span>
                ))}
                <span className="wf-zsaus__val wf-mono">{ts ? formatHMS(soll) : '…'}</span>
                <span className="wf-zsaus__val wf-mono wf-zsaus__ist">{ts ? formatHMS(ist) : '…'}</span>
                <span className={`wf-zsaus__val wf-mono ${saldo.cls}`.trim()}>{ts ? saldo.text : '…'}</span>
                <span className="wf-zsaus__edit">
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    aria-label={`Buchungen von ${entry.name} korrigieren`}
                    title="Buchungen korrigieren (Nachweis)"
                    onClick={() => onCorrect(entry.userId)}
                  >
                    Korrigieren
                  </button>
                </span>
              </div>
            )
          })}
          <div className="wf-zsaus__foot">
            <span>„–" = keine Stempel · Saldo bezieht sich auf den laufenden Zeitraum</span>
            <span>Kumuliertes Überstundenkonto folgt (Backend-Welle B)</span>
          </div>
        </div>
      ) : null}

      <p className="hint">„Korrigieren" öffnet den Monatsnachweis des Mitarbeiters zum Entwerten/Nachtragen.</p>

      {/* § 3 ArbZG: der Acht-Stunden-Durchschnitt hängt an einem rollierenden Fenster von 24 Wochen bzw.
          sechs Monaten — er hat deshalb NICHTS mit der Wochen-/Monats-Auswahl oben zu tun und steht bewusst
          als eigene Karte darunter. Zeigt nur die Auffälligen; der Rechenweg steht im Nachweis. */}
      <ArbzgAverageTeamCard userIds={ids} names={new Map(staff.map((e) => [e.userId, e.name]))} enabled={staff.length > 0} />

      <PayrollExportCard />
    </div>
  )
}

/** Lohn-Export Stufe A: Summenblatt-CSV aus den eingefrorenen Monats-Salden — Link direkt auf die API
 *  (Session-Cookie reist mit); der Server verweigert ohne Hard-Close mit klarem Text. */
function PayrollExportCard() {
  const [month, setMonth] = useState(() => previousPeriod(localDayKey(Date.now()).slice(0, 7)))
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Review-Fund: ein <a download> verschluckt die 400-Meldung („bei wem fehlt der Abschluss") — daher
  // fetch + Blob, damit der Guard-Text mit Namen im UI landet.
  const download = async (): Promise<void> => {
    setErr(null)
    setBusy(true)
    try {
      const res = await fetch(`/api/payroll/export?month=${month}`)
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { error?: string } | null
        setErr(j?.error ?? 'Export fehlgeschlagen — bitte erneut versuchen.')
        return
      }
      const blob = await res.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `lohnexport_${month}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch {
      setErr('Export fehlgeschlagen — keine Verbindung?')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="wf-card" role="region" aria-label="Lohn-Export">
      <h3 className="wf-card__title">Lohn-Export (CSV fürs Lohnbüro)</h3>
      <p className="sub">
        Monats-Summenblatt je Mitarbeiter aus den ABGESCHLOSSENEN Zeitkonten (Soll/Ist/Überstunden/Saldo,
        Urlaubs-/Krank-Tage). Erst Monat abschließen, dann exportieren.
      </p>
      <div className="wf-absform">
        <label className="wf-field">
          <span className="wf-label">Monat</span>
          <input className="wf-input" type="month" aria-label="Export-Monat" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <button type="button" className="wf-btn" onClick={() => void download()} disabled={busy || !month}>
          CSV herunterladen
        </button>
      </div>
      {err ? (
        <p className="wf-absform__err" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  )
}
